import { useMemo } from 'react';

// Starter action chips shown on the empty-chat welcome screen (ChatGPT custom-GPT style) AND
// (compact variant) the floating ChatWidget's empty-thread panel. Single source of truth (T4)
// so the two surfaces can never diverge again — ChatWidget previously had no chips at all, and
// two of ChatPage's chips used to route to a client-only toast + navigate() that silently killed
// the conversation (see in_progress/ai-v2-updates/status.md section 8 for why that toast existed
// in the first place — do not reintroduce a client-only chat bubble for a redirect announcement).
//
//   type: 'prompt' → the caller (`onSend`) sends `payload` immediately as a user message — no
//                     prefill-then-Enter step. `payload` is the ACTUAL text sent; it does not have
//                     to match the button's visible `label` (see the "own documents" chip below,
//                     which sends a more explicit rephrasing than what's printed on the button).
//   type: 'action' → the caller (`onAction`) runs a local flow keyed by `payload` (currently only
//                     the passport-upload-panel shortcut — untouched by this task, see T5).
// `icon` is a Material Symbols glyph rendered in the brand accent (pear) on a dark (viza-black) chip.
export function useStarterChips(countryDisplayName) {
  // Resolve the selected country's real Turkish display name (not the slug — slugs are
  // accent-stripped/abbreviated, e.g. "isvec" -> "İsveç", "amerika" -> "Amerika Birleşik
  // Devletleri"), passed in by the caller. Falls back to generic phrasing if not yet known.
  const documentsChipLabel = countryDisplayName
    ? `${countryDisplayName} vizesi için hangi belgeler gerekiyor?`
    : 'Vizem için hangi belgeler gerekiyor?';

  return useMemo(() => ([
    {
      label: documentsChipLabel,
      type: 'prompt',
      payload: documentsChipLabel,
      icon: 'checklist',
    },
    {
      // Restores the old "Pasaportumu analiz et" one-click shortcut (opens the passport
      // upload panel directly) under new question-style wording — the label reads as a
      // question, but the click still jumps straight to the panel rather than sending a chat
      // message, per product decision (shortcuts must not silently disappear just because the
      // chip's wording changed).
      label: 'Pasaportum hakkında sorularım var.',
      type: 'action',
      payload: 'passport',
      icon: 'badge',
    },
    {
      // T4: was a 'navigate' chip (toast + route to /cover-letter, chat history untouched).
      // Now sends in-chat — chat-api deterministically detects the cover-letter request
      // (turn_classifier.detect_cover_letter_intent) and short-circuits to a persisted
      // assistant message + a top-level `redirect: "/cover-letter"` field instead of drafting
      // one inline. The caller (ChatPage/ChatWidget) reads that field and navigates itself.
      label: 'Niyet mektubuna ihtiyacım var',
      type: 'prompt',
      payload: 'Niyet mektubuna ihtiyacım var',
      icon: 'edit_document',
    },
    {
      // T4 / confirmed product decision (issue 5): was a 'navigate' chip to /documents where
      // the conversation simply died. Now stays fully in-chat — chat-api answers using the
      // caller's OWN uploaded document facts (get_document_facts, already wired server-side
      // whenever a country is selected) and never navigates away from /chat. The sent payload
      // is deliberately more explicit than the button label ("Yüklediğim" = "the ones I
      // uploaded") so the assistant reliably talks about the user's own documents rather than
      // documents in general.
      label: 'Belgelerimle ilgili konuşmak istiyorum',
      type: 'prompt',
      payload: 'Yüklediğim belgeler hakkında konuşmak istiyorum',
      icon: 'description',
    },
    {
      label: 'Vize başvuru süreci tam olarak nasıl işleyecek?',
      type: 'prompt',
      payload: 'Vize başvuru süreci tam olarak nasıl işleyecek?',
      icon: 'route',
    },
  ]), [documentsChipLabel]);
}

// Presentational chip list — deliberately dumb: it never sends/navigates/opens anything itself.
//   onSend(text)   — fired for 'prompt' chips; the caller owns the actual send pipeline (and its
//                    guards: isLoading / free-chat-limit / selected-country) so a chip click can
//                    never bypass them.
//   onAction(payload) — fired for 'action' chips.
//   variant: 'page' (ChatPage welcome card, default) | 'compact' (ChatWidget empty-thread panel).
function ChatStarterChips({ chips, onSend, onAction, variant = 'page' }) {
  const isCompact = variant === 'compact';

  const handleClick = (chip) => {
    if (chip.type === 'action') {
      onAction?.(chip.payload);
    } else if (chip.type === 'prompt') {
      onSend?.(chip.payload);
    }
  };

  return (
    <div className={`starter-chips flex flex-wrap ${isCompact ? 'gap-1.5 mt-2' : 'gap-2.5 mt-4'}`}>
      {chips.map((chip) => (
        <button
          key={chip.label}
          type="button"
          onClick={() => handleClick(chip)}
          className={
            isCompact
              ? 'inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold font-sans text-left bg-viza-black text-white border border-viza-black/10 shadow-sm hover:bg-soft-black transition-all cursor-pointer'
              : 'inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold font-sans text-left bg-viza-black text-white border border-viza-black/10 shadow-sm hover:bg-soft-black hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-pear/40 transition-all cursor-pointer'
          }
        >
          <span className="material-symbols-outlined text-pear" style={{ fontSize: isCompact ? '16px' : '18px' }}>
            {chip.icon}
          </span>
          <span>{chip.label}</span>
        </button>
      ))}
    </div>
  );
}

export default ChatStarterChips;
